"use client";

import { type ChangeEvent } from "react";

import type { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  AuthCard,
  AuthHeading,
  PrimaryButton,
  RESIDENT_ROUTES,
  TextInput,
} from "@pulseprep/shared-ui";
import { Form, FormField } from "@pulseprep/shared-ui/components/ui";
import { toast, useInitiateResidentRegistration } from "@pulseprep/shared-ui/hooks";
import { type SignupEmailFormData, signupEmailSchema } from "@pulseprep/shared-ui/schema";
import { useForm } from "react-hook-form";

import { clearSignupOtpResendPersistedEndTime } from "@/components/otp-resend-timer";

export interface SignupEmailClientProps {
  loginHref: Route;
}

export function SignupEmailClient({ loginHref }: SignupEmailClientProps) {
  const router = useRouter();
  const { initiateIndividualSignup, isLoading } = useInitiateResidentRegistration();

  const form = useForm<SignupEmailFormData>({
    resolver: zodResolver(signupEmailSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: SignupEmailFormData) => {
    const email = data.email.trim().toLowerCase();

    try {
      await initiateIndividualSignup({ email });
      clearSignupOtpResendPersistedEndTime(email);
      toast.success("Verification code sent. Check your email.");
      const qs = new URLSearchParams({ email: encodeURIComponent(email) });
      router.push(`${RESIDENT_ROUTES.signupVerify}?${qs.toString()}` as Route);
    } catch {
      /* Hook surfaces API errors via toast */
    }
  };

  return (
    <div data-testid="signup-email-container" className="flex w-full flex-col gap-8 sm:max-w-md">
      <AuthHeading title="Let's Start !" subtitle="Add your email to continue" />

      <AuthCard className="px-4 py-4 sm:px-8 sm:py-8">
        <Form {...form}>
          <form
            data-testid="signup-email-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
            noValidate>
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <TextInput
                  field={{
                    ...field,
                    onChange: (e: ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.value.trimStart()),
                  }}
                  label="Email Address"
                  type="email"
                  placeholder="Enter email"
                  autoComplete="email"
                  error={fieldState.error?.message}
                  testId="signup-email-input"
                />
              )}
            />
            <div className="flex flex-col gap-4">
              <PrimaryButton data-testid="signup-send-otp-btn" type="submit" disabled={isLoading}>
                {isLoading ? "Sending…" : "Send OTP"}
              </PrimaryButton>
              <p className="text-sm text-center text-muted-foreground">
                Already have an account?{" "}
                <Link
                  data-testid="signup-link"
                  href={loginHref}
                  className="font-inter text-auth-label hover:text-brand-navy text-[14px] font-normal tracking-[-0.15px] transition-colors hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </Form>
      </AuthCard>
    </div>
  );
}
